import React, { useState, useEffect, useContext, useRef } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBell, 
    faPlus, 
    faExternalLinkAlt, 
    faExclamationTriangle,
    faArrowLeft,
    faEdit,
    faTrash,
    faDownload,
    faEye
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './header';
import Footer from './footer';
import { useCustomAlert } from './CustomAlert';

const DepartmentAnnouncements = () => {
    const { user } = useContext(MyContext);
    const { groupId, departmentId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    
    const [department, setDepartment] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('member');
    const [isDepartmentLeader, setIsDepartmentLeader] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (user && user.user_id && departmentId) {
            loadDepartmentData();
            loadAnnouncements();
        }
    }, [user, departmentId, currentPage]);

    const loadDepartmentData = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_department_detail.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                setDepartment(data.data.department);
                setIsDepartmentLeader(data.data.is_department_leader);
            } else {
                setError(data.messages || 'Failed to load department data');
            }
        } catch (err) {
            setError('Failed to load department data');
            console.error('Error loading department data:', err);
        }
    };

    const loadAnnouncements = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_department_announcements.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id,
                    page: currentPage,
                    limit: 10
                })
            });

            const data = await response.json();
            if (data.success) {
                setAnnouncements(data.data.announcements || []);
                setTotalPages(data.data.pagination.total_pages || 1);
                setUserRole(data.data.user_role);
            } else {
                setError(data.messages || 'Failed to load announcements');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error loading announcements:', err);
        } finally {
            setLoading(false);
        }
    };

    const trackAnnouncementView = async (announcementId) => {
        try {
            const response = await fetch(`${backendUrl}/track_announcement_view.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    announcement_id: announcementId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success && !data.data.already_viewed) {
                console.log(`View tracked for announcement ${announcementId}`);
            }
        } catch (err) {
            console.error('Error tracking announcement view:', err);
        }
    };

    // Helper function to safely parse dates for Safari compatibility
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        if (dateString instanceof Date) return dateString;
        
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
        if (match) {
            const [, year, month, day, hour, minute, second] = match;
            const date = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                parseInt(hour, 10),
                parseInt(minute, 10),
                parseInt(second, 10)
            );
            
            return isNaN(date.getTime()) ? null : date;
        }
        
        try {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        
        const date = parseDate(timestamp);
        if (!date) return 'Invalid date';
        
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        
        return date.toLocaleDateString();
    };

    if (loading && !department) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading department announcements...</div>
            </div>
        );
    }

    if (error || !department) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error || 'Department not found'}
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            
            <div className="container-fluid p-3 flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <div>
                            <h2 className="mb-0">
                                <FontAwesomeIcon icon={faBell} className="me-2" />
                                Department Announcements
                            </h2>
                            <small className="text-muted">{department.name}</small>
                        </div>
                    </div>
                    {isDepartmentLeader && (
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Create Announcement
                        </button>
                    )}
                </div>

                {/* Announcements List */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-3">Loading announcements...</div>
                    </div>
                ) : announcements.length === 0 ? (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faBell} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">No announcements yet</h5>
                        <p className="text-muted">
                            {isDepartmentLeader 
                                ? 'Create the first announcement to get started!' 
                                : 'No announcements have been created for this department yet.'
                            }
                        </p>
                        {isDepartmentLeader && (
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Create First Announcement
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="row">
                        {announcements.map((announcement) => (
                            <div key={announcement.id} className="col-12 mb-4">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-2">
                                                    {announcement.is_important && (
                                                        <FontAwesomeIcon 
                                                            icon={faExclamationTriangle} 
                                                            className="text-warning me-2" 
                                                        />
                                                    )}
                                                    <h5 className="card-title mb-0">{announcement.title}</h5>
                                                </div>
                                                <div className="d-flex align-items-center text-muted small mb-2">
                                                    <span className="me-3">
                                                        By {announcement.sender_first_name} {announcement.sender_last_name}
                                                    </span>
                                                    <span>{formatTime(announcement.created_at)}</span>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <span className="badge bg-secondary me-2">
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    {announcement.view_count} views
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="announcement-content">
                                            <p className="card-text">{announcement.message}</p>
                                            
                                            {announcement.image_url && (
                                                <div className="mb-3">
                                                    <img 
                                                        src={announcement.image_url} 
                                                        alt="Announcement flyer" 
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '300px', objectFit: 'cover' }}
                                                    />
                                                    <div className="mt-2">
                                                        <a 
                                                            href={announcement.image_url} 
                                                            download={`announcement-${announcement.id}-flyer.jpg`}
                                                            className="btn btn-sm btn-outline-secondary"
                                                        >
                                                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                                                            Download Image
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {announcement.has_url && (
                                                <div className="mb-3">
                                                    <a 
                                                        href={announcement.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="btn btn-primary"
                                                    >
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                                        {announcement.url_text || 'View Link'}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <nav>
                            <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                            className="page-link" 
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </button>
                                    </li>
                                ))}
                                
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link" 
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>

            <Footer />

            {/* Create Announcement Modal */}
            {showCreateModal && (
                <CreateAnnouncementModal 
                    departmentId={departmentId}
                    onClose={() => setShowCreateModal(false)}
                    onAnnouncementCreated={loadAnnouncements}
                />
            )}

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Create Announcement Modal Component
const CreateAnnouncementModal = ({ departmentId, onClose, onAnnouncementCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        url: '',
        url_text: '',
        is_important: false
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        
        if (!formData.message.trim()) {
            setError('Message is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = null;
            
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('department_id', departmentId);
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                } else {
                    setError(uploadData.messages || 'Failed to upload image');
                    return;
                }
            }

            const announcementData = {
                department_id: departmentId,
                sender_id: user.user_id,
                title: formData.title.trim(),
                message: formData.message.trim(),
                url: formData.url.trim() || null,
                url_text: formData.url_text.trim() || null,
                is_important: formData.is_important ? 1 : 0,
                image_url: imageUrl
            };
            
            const response = await fetch(`${backendUrl}/create_department_announcement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcementData)
            });

            const data = await response.json();

            if (data.success) {
                onAnnouncementCreated();
                onClose();
                showAlert('Announcement created successfully!', 'success');
            } else {
                setError(data.messages || 'Failed to create announcement');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating announcement:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Department Announcement</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="title" className="form-label">Title *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    placeholder="Enter announcement title"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="message" className="form-label">Message *</label>
                                <textarea
                                    className="form-control"
                                    id="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    placeholder="Enter announcement message"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="image" className="form-label">Flyer/Image (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="image"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(null);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">Link URL (Optional)</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    id="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                                    placeholder="https://example.com"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url_text" className="form-label">Link Text (Optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="url_text"
                                    value={formData.url_text}
                                    onChange={(e) => setFormData({...formData, url_text: e.target.value})}
                                    placeholder="Click here to view"
                                />
                            </div>

                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_important"
                                        checked={formData.is_important}
                                        onChange={(e) => setFormData({...formData, is_important: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="is_important">
                                        Mark as important
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Announcement'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

export default DepartmentAnnouncements;
